import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, primeCsrf } from './api';
import type { Paginated } from './paginated';
import type { ClientRequestData } from '@/types/api';

type RequestFilters = { status?: string; type?: string; page?: number };

export type NewRequestInput = {
  client_id: number;
  type: string;
  subject: string;
  body: string;
  channel: string;
};

export function useRequests(filters: RequestFilters = {}) {
  return useQuery({
    queryKey: ['requests', filters],
    queryFn: async (): Promise<Paginated<ClientRequestData>> => {
      const { data } = await api.get<Paginated<ClientRequestData>>('/v1/requests', { params: filters });
      return data;
    },
  });
}

export function useCreateRequest() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: NewRequestInput): Promise<ClientRequestData> => {
      await primeCsrf();
      const { data } = await api.post<ClientRequestData>('/v1/requests', input);
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['requests'] }),
  });
}

export function useUpdateRequestStatus() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, status }: { id: number; status: string }): Promise<ClientRequestData> => {
      await primeCsrf();
      const { data } = await api.patch<ClientRequestData>(`/v1/requests/${id}`, { status });
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['requests'] }),
  });
}

/**
 * Take a request out of the queue entirely. Closing is the normal end of a
 * request; this is for the ones that should not be in the queue at all. Soft
 * deletes, so what was said survives.
 */
export function useDeleteRequest() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: number): Promise<void> => {
      await primeCsrf();
      await api.delete(`/v1/requests/${id}`);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['requests'] }),
  });
}
